import { ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { Subscription } from 'rxjs';

interface Option {
  key: string;
  value: string;
  isChecked?: boolean;
  highlighted?: string;
  relevance?: number
}

@Component({
  selector: 'app-custom-search-dropdown',
  templateUrl: './custom-search-dropdown.component.html',
  styleUrls: ['./custom-search-dropdown.component.scss']
})
export class CustomSearchDropdownComponent implements OnInit {
  @Input() options: Option[] = [];
  @Input() control!: FormControl;
  @Input() placeholder!: string;
  @Input() chosenItemsPlaceholder!: string;
  @Input() searchPlaceholder: string = 'Search...';
  @Input() noResultsText: string = 'No results found';
  @Input() clearButtonText: string = 'Clear';
  @Input() submitButtonText: string = 'Submit';
  @Input() immediateSelect?: boolean;
  @Input() closeAfterImmediateSelect?: boolean = false;
  @Input() disabled?: boolean = false;
  @Input() wrapperClasses: string[] = [];
  @Output() onSelect = new EventEmitter<any[]>();
  
  searchTerm = '';
  filteredOptions: Option[] = [];
  isOpen: boolean = false;

  // Close when clicking outside
  @HostListener('document:mousedown', ['$event'])
  onOutsideClick(event: MouseEvent) {
    if (!this.isOpen) return;
    if (!this.host.nativeElement.contains(event.target)) {
      this.closeDropdown();
    }
  }

  private valueSubscription?: Subscription;
  constructor(private host: ElementRef, private cd: ChangeDetectorRef) {}

  ngOnInit() {
    if (this.control) {
      this.valueSubscription = this.control.valueChanges.subscribe(() => {
        this.updateFilteredOptions();
      });
    }

    this.normalizeOptions();
    this.updateFilteredOptions();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['options']) {
      this.normalizeOptions();
      this.updateFilteredOptions();
    }
  }

  ngOnDestroy() {
    this.valueSubscription?.unsubscribe();
  }

  private normalizeOptions() {
    this.options = this.options.map(x => ({
      ...x,
      isChecked: false
    }));
  }

  private updateFilteredOptions() {
    this.filteredOptions = [...this.options];
    this.cd.markForCheck();
  }

  getSelectedItemsText() {
    if(!this.chosenItemsPlaceholder || this.control.value.includes("-1")) return "";
    const selectedCount = this.control.value.filter((x: string) => x != "-1").length;
    return `(${this.chosenItemsPlaceholder.replace("{0}", selectedCount)})`;
  }

  clearSelection() {
    this.options.forEach(x => x.isChecked = false);
    this.searchTerm = "";
    this.filterOptions();
  }

  getOptionsText() {
    const selectedItems = this.options.filter(x => this.control.value?.includes(x.key));
    if(selectedItems.some(x => x.key === "-1")) return this.options.find(o => o.key === "-1")?.value;

    return selectedItems.map(x => x.value).join(", ");
  }

  toggleOpen() {
    this.options.forEach(x => x.isChecked = this.control.value?.includes(x.key))
    this.isOpen = !this.isOpen;
  }

  onOptionChange(opt: any) {
    if (opt.key === "-1") {
      // User toggled "All"
      const newState = opt.isChecked;

      this.options.forEach(o => {
        o.isChecked = newState;
      });
    }
    else {
      const allOption = this.options.find(o => o.key === "-1");
      const allSelected = this.options
        .filter(o => o.key !== "-1")
        .every(o => o.isChecked);
      if (allOption) {
        allOption.isChecked = allSelected;
      }
    }
    if(this.immediateSelect)
      this.submitSelection()
  }

  filterOptions() {
    const searchTerms = this.searchTerm
      .split(' ')
      .filter(Boolean);

    if (!searchTerms.length) {
      this.options.forEach(x => {
        x.relevance = 0;
        x.highlighted = undefined;
      });
      this.filteredOptions = [...this.options];
      return;
    }

    let filtered = this.options.filter(option =>
      option.key != "-1" &&
      searchTerms.some(term =>
        option.value.toLowerCase().includes(term)
      )
    );

    filtered.forEach(option => {
      const valueLower = option.value.toLowerCase();
      let relevance = 0;
      searchTerms.forEach(term => { if(valueLower.includes(term)) relevance += 1 });
      option.relevance = relevance;
      option.highlighted = this.getHighlighted(option.value, searchTerms);
    });

    this.filteredOptions = filtered.sort((a, b) => (b.relevance ?? 0) - (a.relevance ?? 0));
  }

  private getHighlighted(value: string, searchTerms: string[]): string {
    let highlighted = value;
    searchTerms.forEach(term => {
      if (!term) return;
      const regex = new RegExp(`(${term})`, 'ig');
      highlighted = highlighted.replace(regex, '<mark>$1</mark>');
    });
    return highlighted;
  }

  submitSelection() {
    const selectedItems = this.options.filter(x => x.isChecked).map(x => x.key);
    this.control.setValue(selectedItems);
    this.onSelect.emit(selectedItems);
    if(!this.immediateSelect || (this.immediateSelect && this.closeAfterImmediateSelect))
    this.closeDropdown();
  }

  closeDropdown() {
    this.searchTerm = "";
    this.isOpen = false;
  }
}
